import { useMutation } from '@tanstack/react-query';
import { motion } from 'motion/react';
import React, { useState } from 'react';

import { webauthnBeginLogin, webauthnFinishLogin } from '@column/api';
import { Button } from '@column/column-ui-kit';

import { getPublicKeyCredential, isWebAuthnSupported } from '~/lib/webauthn';
import { ChallengeName, DashboardLoginResponse, EmailConfirmationStatus, MFAStatus } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';

interface PasskeyAuthButtonProps {
  onSuccess: (response: DashboardLoginResponse) => void;
}

const CompletionIndicator: React.FC = () => (
  <motion.svg className="size-4" aria-hidden="true" viewBox="0 0 16 16" fill="none">
    <motion.circle
      cx="8"
      cy="8"
      r="6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
    />
    <motion.path
      d="m5.25 8 1.85 1.85 3.65-3.65"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial={{ opacity: 0, pathLength: 0 }}
      animate={{ opacity: 1, pathLength: 1 }}
      transition={{ delay: 0.2, duration: 0.25, ease: 'easeOut' }}
    />
  </motion.svg>
);

export const PasskeyAuthButton: React.FC<PasskeyAuthButtonProps> = ({ onSuccess }) => {
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const isSupported = isWebAuthnSupported();
  const [authenticatedResponse, setAuthenticatedResponse] = useState<DashboardLoginResponse>();

  const loginMutation = useMutation({
    mutationFn: async () => {
      const { data: login } = await webauthnBeginLogin({ throwOnError: true });
      const credential = await getPublicKeyCredential(login.options);
      const { data: response } = await webauthnFinishLogin({
        body: {
          credential,
        },
        throwOnError: true,
      });

      return {
        challengeName: ChallengeName[response.challenge_name],
        dashboardUserId: response.dashboard_user_id,
        defaultPlatformId: response.default_platform_id,
        emailConfirmationStatus: EmailConfirmationStatus.NOT_REQUIRED,
        mfaStatus: MFAStatus[response.mfa_status],
        phoneNumber: response.phone_number,
        sessionId: response.session_id,
      };
    },
    onSuccess: (response) => {
      setAuthenticatedResponse(response);
      onSuccess(response);
    },
    onError: (error: unknown) => {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        return;
      }

      const content = error instanceof Error ? error.message : 'Failed to sign in with a passkey. Please try again.';
      addDangerNotification({ content, display: 'page-login' });
    },
  });

  return (
    <Button
      type="button"
      fullWidth
      variant="secondary"
      ariaLabel={authenticatedResponse ? 'Passkey verified' : undefined}
      isDisabled={!isSupported || loginMutation.isPending || !!authenticatedResponse}
      isLoading={loginMutation.isPending || !!authenticatedResponse}
      content={
        authenticatedResponse ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            <CompletionIndicator />
          </div>
        ) : undefined
      }
      onClick={() => {
        if (!authenticatedResponse) {
          loginMutation.mutate();
        }
      }}
    >
      Sign in with a passkey
    </Button>
  );
};
